import React, { useEffect, useState } from 'react';

/**
 * Material Symbols Icon Component
 * Uses Google Material Symbols with configurable weight, grade, and fill
 * 
 * @param {string} icon - The icon name (e.g., 'home', 'settings', 'favorite')
 * @param {number} weight - Font weight (100-700, default: 300)
 * @param {number} grade - Grade adjustment (-50 to 200, default: 0)
 * @param {number} fill - Fill state (0 or 1, default: 0)
 * @param {number} size - Icon size in pixels (default: 24)
 * @param {string} color - Icon color (CSS color value)
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 */
const MATERIAL_SYMBOLS_FONT_FAMILY = 'Material Symbols Outlined';

const MaterialIcon = ({ 
  icon, 
  weight = 300, 
  grade = 0, 
  fill = 0, 
  size = 24,
  color,
  className = '',
  style = {},
  ...props 
}) => {
  const [isFontReady, setIsFontReady] = useState(() => {
    // If the Font Loading API isn't available, fall back to rendering the icon as-is.
    if (typeof document === 'undefined' || !document.fonts) return true;
    try {
      return document.fonts.check(`${size}px "${MATERIAL_SYMBOLS_FONT_FAMILY}"`);
    } catch (_) {
      return true;
    }
  });

  useEffect(() => {
    if (typeof document === 'undefined' || !document.fonts) return;

    let cancelled = false;
    const fontString = `${size}px "${MATERIAL_SYMBOLS_FONT_FAMILY}"`;

    try {
      if (document.fonts.check(fontString)) {
        setIsFontReady(true);
        return;
      }
    } catch (_) {
      setIsFontReady(true);
      return;
    }

    document.fonts
      .load(fontString, icon || ' ')
      .then(() => {
        if (!cancelled) setIsFontReady(true);
      })
      .catch(() => {
        // Don't block rendering forever if font loading fails.
        if (!cancelled) setIsFontReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [icon, size]);

  const iconStyle = {
    fontFamily: MATERIAL_SYMBOLS_FONT_FAMILY,
    fontWeight: weight,
    fontStyle: 'normal',
    fontSize: `${size}px`,
    lineHeight: 1,
    letterSpacing: 'normal',
    textTransform: 'none',
    display: 'inline-block',
    whiteSpace: 'nowrap',
    wordWrap: 'normal',
    direction: 'ltr',
    fontFeatureSettings: `'liga'`,
    WebkitFontSmoothing: 'antialiased',
    fontVariationSettings: `'FILL' ${fill}, 'wght' ${weight}, 'GRAD' ${grade}, 'opsz' 24`,
    color: color || 'inherit',
    ...style
  };

  if (!isFontReady) {
    // Prevent the icon name (ligature text) from flashing while the font loads.
    const placeholderStyle = {
      display: 'inline-block',
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: Math.max(4, Math.round(size / 6)),
      backgroundColor: '#d9d9d9',
      opacity: 0.7,
      verticalAlign: 'text-bottom',
      ...style,
    };

    return (
      <span
        className={className}
        style={placeholderStyle}
        {...props}
      />
    );
  }

  return (
    <span 
      className={`material-symbols-outlined ${className}`} 
      style={iconStyle}
      {...props}
    >
      {icon}
    </span>
  );
};

export default MaterialIcon;

